<template>
  <div class="booking-intro">
    <!-- 图标雪碧图：id 统一加 bk- 前缀，避免与主应用/子应用既有 id 冲突 -->
    <svg class="icon-sprite" aria-hidden="true">
      <symbol id="bk-i-arrow-up-right" viewBox="0 0 24 24"><path d="M7 7h10v10" /><path d="M7 17 17 7" /></symbol>
      <symbol id="bk-i-arrow-right" viewBox="0 0 24 24"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></symbol>
      <symbol id="bk-i-layers" viewBox="0 0 24 24">
        <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
        <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
        <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
      </symbol>
      <symbol id="bk-i-calendar-clock" viewBox="0 0 24 24">
        <path d="M16 14v2.2l1.6 1" />
        <path d="M16 2v4" />
        <path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5" />
        <path d="M3 10h5" />
        <path d="M8 2v4" />
        <circle cx="16" cy="16" r="6" />
      </symbol>
      <symbol id="bk-i-circle-alert" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" x2="12" y1="8" y2="12" />
        <line x1="12" x2="12.01" y1="16" y2="16" />
      </symbol>
      <symbol id="bk-i-file-down" viewBox="0 0 24 24">
        <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
        <path d="M14 2v5a1 1 0 0 0 1 1h5" />
        <path d="M12 18v-6" />
        <path d="m9 15 3 3 3-3" />
      </symbol>
      <symbol id="bk-i-rows" viewBox="0 0 24 24">
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M21 9H3" />
        <path d="M21 15H3" />
      </symbol>
      <symbol id="bk-i-file-plus" viewBox="0 0 24 24">
        <path d="M11.35 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v5.35" />
        <path d="M14 2v5a1 1 0 0 0 1 1h5" />
        <path d="M14 19h6" />
        <path d="M17 16v6" />
      </symbol>
      <symbol id="bk-i-scroll-text" viewBox="0 0 24 24">
        <path d="M15 12h-5" />
        <path d="M15 8h-5" />
        <path d="M19 17V5a2 2 0 0 0-2-2H4" />
        <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" />
      </symbol>
    </svg>

    <section class="hero">
      <div class="hero-wash" aria-hidden="true"></div>
      <div class="hero-inner">
        <h1>订舱助手</h1>
        <p class="hero-lead">从多票建单到放舱结果，<br />每一票进度都看得见。</p>
        <p class="hero-copy">基于企业船司账号发起订舱，集中处理提交、异常与预配监测，让团队在一张工作台上协同推进。</p>
        <p v-if="bookingAccess === 'COMPANY_UNAVAILABLE'" class="hero-copy">企业已停用，请联系客服。</p>
        <p v-if="bookingAccess === 'LOAD_FAILED'" class="hero-copy">企业状态获取失败，请重试。</p>
        <div class="hero-actions">
          <button class="bk-button bk-button--primary" type="button" :disabled="bookingAccess === 'LOADING' || bookingAccess === 'COMPANY_UNAVAILABLE'" @click="enterBooking">
            {{ bookingActionLabel }}
            <svg class="icon" aria-hidden="true"><use href="#bk-i-arrow-up-right" /></svg>
          </button>
          <button class="bk-button bk-button--outline" type="button" @click="scrollToPreview">
            查看产品界面
            <svg class="icon" aria-hidden="true"><use href="#bk-i-arrow-right" /></svg>
          </button>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="section-inner">
        <div class="section-heading">
          <div>
            <p class="section-kicker">产品能力 / CAPABILITIES</p>
            <h2>把订舱进度，放回可掌握的流程里</h2>
          </div>
          <p>不只发起申请，也持续跟进每票的执行结果。关键资料、状态和操作记录都留在同一处。</p>
        </div>
        <div class="capabilities">
          <article v-for="item in capabilities" :key="item.title" class="capability">
            <span class="feature-icon" :class="item.tone">
              <svg class="icon" aria-hidden="true"><use :href="`#bk-i-${item.icon}`" /></svg>
            </span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section workflow-section">
      <div class="section-inner">
        <div class="section-heading">
          <div>
            <p class="section-kicker">业务流程 / WORKFLOW</p>
            <h2>从创建到放舱，环环清楚</h2>
          </div>
          <p>一条主线串起建单、执行、监测和结果；每票独立流转，不因同批其他票的异常失去进度。</p>
        </div>
        <ol class="workflow-list">
          <li v-for="step in workflow" :key="step.number">
            <span class="step-number">{{ step.number }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section ref="preview" class="section preview-section">
      <div class="section-inner">
        <p class="section-kicker">产品界面 / PREVIEW</p>
        <div class="preview-tabs" role="tablist" aria-label="界面预览">
          <button
            v-for="(item, index) in previews"
            :id="`booking-tab-${item.key}`"
            :key="item.key"
            type="button"
            role="tab"
            :aria-selected="String(activePreview === item.key)"
            :tabindex="activePreview === item.key ? 0 : -1"
            aria-controls="booking-preview-panel"
            @click="selectPreview(item.key)"
            @keydown="onTabKeydown($event, index)"
          >
            <svg class="icon" aria-hidden="true"><use :href="`#bk-i-${item.icon}`" /></svg>
            {{ item.title }}
          </button>
        </div>
        <div id="booking-preview-panel" class="preview-panel" role="tabpanel" tabindex="0" :aria-labelledby="`booking-tab-${activePreview}`">
          <div class="preview-copy">
            <span class="preview-index">{{ activePreviewItem.index }}</span>
            <h3>{{ activePreviewItem.caption }}</h3>
            <p>{{ activePreviewItem.description }}</p>
          </div>
          <div class="preview-image-wrap">
            <div class="preview-art" role="img" :aria-label="`${activePreviewItem.title}的抽象界面示意`">
              <BookingPreviewMock :type="activePreview" :title="activePreviewItem.title" />
            </div>
          </div>
        </div>
        <p class="preview-note">界面为产品示意，未展示完整字段与订单数据；可用额度以企业配置为准。</p>
      </div>
    </section>

    <ContactUs compact />
  </div>
</template>

<script>
import ContactUs from '@/components/ContactUs'

import BookingPreviewMock from './components/BookingPreviewMock'

// 主应用顶栏为 fixed 布局（components/header/style.scss → .public-header 固定 100px），
// 锚点滚动需要让出这段高度
const PORTAL_HEADER_HEIGHT = 100

/**
 * 订舱助手产品介绍页（未认证或企业不可用时访问 /introduce/booking）
 * 顶栏与页脚由主应用 App.vue 统一渲染，本页不重复实现导航。
 */
export default {
  name: 'Booking',
  components: { ContactUs, BookingPreviewMock },
  data() {
    return {
      activePreview: 'list',
      capabilities: [
        { title: '多票建单', tone: 'blue', icon: 'layers', desc: '同一批次最多创建 50 票。常用资料保存为模板，每票拥有独立 Job No. 与状态。' },
        { title: '灵活提交', tone: 'orange', icon: 'calendar-clock', desc: '支持立即提交和单次定时。定时任务到点逐票校验，有效票继续，失败票单独处理。' },
        { title: '异常有后续', tone: 'coral', icon: 'circle-alert', desc: '区分订舱异常与放舱异常，按状态提供编辑、重新订舱、重启监测或作废入口。' },
        { title: '结果可追溯', tone: 'green', icon: 'file-down', desc: '查看每票操作人、时间与官网结果；支持下载已取得的订舱回执及预配回执。' }
      ],
      workflow: [
        { number: '01', title: '准备订舱资料', desc: '选择船司账号，填写航线、箱型箱量与货物资料，可复用企业模板。' },
        { number: '02', title: '提交或设置定时', desc: '按票校验资料。通过校验的草稿可立即提交，或组成单次定时任务。' },
        { number: '03', title: '跟进执行结果', desc: '在统一列表查看排队、订舱中、订舱成功及需要处理的异常。' },
        { number: '04', title: '监测与下载回执', desc: '订舱成功后进入预配监测，确认放舱后获取支持下载的回执。' }
      ],
      previews: [
        { key: 'list', icon: 'rows', title: '订舱列表', index: '01 / 03', caption: '一屏查看每票进度', description: '按状态筛选待关注、处理中和放舱成功的订单，批量操作与单票处理都从列表进入。' },
        { key: 'form', icon: 'file-plus', title: '新建订舱', index: '02 / 03', caption: '批量建单，信息各有归属', description: '按船司账号填写资料，同批次生成多票 Job No.，右侧同步汇总本次订舱与预配监测设置。' },
        { key: 'records', icon: 'scroll-text', title: '操作记录', index: '03 / 03', caption: '关键动作与官网结果完整留痕', description: '从创建、提交、订舱成功到启动监测、放舱成功，操作人、时间和原始消息可以回看。' }
      ]
    }
  },
  created() {
    this.$store.dispatch('bookingAccess/refresh', true)
  },
  activated() {
    this.$store.dispatch('bookingAccess/refresh', true)
  },
  computed: {
    bookingAccess() { return this.$store.getters['bookingAccess/access'] },
    bookingActionLabel() {
      return ({ LOADING: '正在获取企业状态', ANONYMOUS: '登录并认证企业', NEED_CERTIFICATION: '前往企业认证', COMPANY_UNAVAILABLE: '企业不可用', ELIGIBLE: '进入订舱助手', LOAD_FAILED: '重试获取企业状态' })[this.bookingAccess]
    },
    activePreviewItem() {
      return this.previews.find(item => item.key === this.activePreview) || this.previews[0]
    }
  },
  methods: {
    selectPreview(key) {
      this.activePreview = key
    },
    onTabKeydown(event, index) {
      const { key } = event
      const supported = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
      if (!supported.includes(key)) return
      event.preventDefault()

      const last = this.previews.length - 1
      let next = index
      if (key === 'Home') next = 0
      else if (key === 'End') next = last
      else next += key === 'ArrowRight' ? 1 : -1
      // 首尾环形切换
      if (next < 0) next = last
      if (next > last) next = 0

      const tab = this.previews[next]
      this.activePreview = tab.key
      this.$nextTick(() => {
        // 用 id 定位而不是 $refs：v-for 里的 ref 是数组，Vue 不保证其顺序
        const el = document.getElementById(`booking-tab-${tab.key}`)
        el && el.focus()
      })
    },
    scrollToPreview() {
      const target = this.$refs.preview
      if (!target) return
      const top = target.getBoundingClientRect().top + window.pageYOffset - PORTAL_HEADER_HEIGHT
      window.scrollTo({ top, behavior: 'smooth' })
    },
    async enterBooking() {
      const state = await this.$store.dispatch('bookingAccess/refresh', true)
      const path = state === 'ELIGIBLE' ? '/booking' : state === 'ANONYMOUS' ? '/user/register' : state === 'NEED_CERTIFICATION' ? '/user/company/certification' : null
      if (path) this.$router.push(path)
    }
  }
}
</script>

<style lang="scss" scoped>
$ink: #17294b;
$muted: #53647e;
$blue: #1767db;
$line: #dbe6f3;
$pale: #edf7ff;

.booking-intro {
  color: $ink;
  background: #fff;
  /* 对齐宣传页原型：body 的系统字体栈 + 基础行高/字距；按钮默认不继承字体，需显式声明 */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft YaHei', Arial, sans-serif;
  line-height: 1.6;
  letter-spacing: 0;

  button,
  a {
    font-family: inherit;
  }
}

.icon-sprite {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}

.icon {
  width: 18px;
  height: 18px;
  flex: none;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.section-inner,
.hero-inner {
  width: 1200px;
  margin: 0 auto;
}

/* 产品主视觉：港区实景图 + 左侧淡色蒙层（.hero-wash），保证深色文案在照片上的可读性。
   实景图放在本地 assets；若后续改走 OSS，把 url 换成 ${ossUrl}/common/introduce/booking-hero.jpg 即可 */
.hero {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 570px;
  background: #d8ecfc url('~@/assets/images/booking-hero.jpg') center 52% / cover no-repeat;

  .hero-wash {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(
      90deg,
      rgba($pale, 0.5) 0%,
      rgba($pale, 0.24) 40%,
      rgba($pale, 0.04) 72%,
      rgba($pale, 0) 100%
    );
  }

  .hero-inner {
    position: relative;
    z-index: 1;
    padding-top: 58px;
    padding-bottom: 66px;
  }

  h1 {
    /* 显式左对齐：公告等 v-html 内容会往主文档塞全局样式（如 h1{text-align:center}），
       这里兜底，避免登录后弹过公告导致标题被居中 */
    text-align: left;
    margin: 0 0 14px;
    font-size: 68px;
    line-height: 1.12;
    font-weight: 800;
    color: #102a50;
  }

  .hero-lead {
    max-width: 610px;
    margin: 0;
    font-size: 27px;
    line-height: 1.45;
    font-weight: 700;
    color: #16375d;
  }

  .hero-copy {
    max-width: 500px;
    margin: 21px 0 30px;
    font-size: 17px;
    line-height: 1.8;
    color: #24496d;
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }
}

.bk-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 46px;
  padding: 11px 19px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  .icon {
    width: 17px;
    height: 17px;
  }
}

.bk-button--primary {
  border: 1px solid #ed9a1f;
  background: #ffae35;
  color: #173255;
  box-shadow: 0 4px 16px rgba(198, 117, 12, 0.15);

  &:hover {
    background: #ffbb55;
  }
}

.bk-button--outline {
  border: 1px solid rgba(26, 74, 123, 0.52);
  background: rgba(255, 255, 255, 0.72);
  color: #173d66;

  &:hover {
    background: #fff;
  }
}

.section {
  padding: 78px 0;
}

.section-kicker {
  margin: 0;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.4;
  text-transform: uppercase;
  color: $blue;
}

.section-heading {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.65fr);
  align-items: end;
  gap: 72px;
  margin-bottom: 42px;

  h2 {
    max-width: 720px;
    margin: 12px 0 0;
    font-size: 38px;
    line-height: 1.28;
    font-weight: 800;
    color: $ink;
  }

  > p {
    max-width: 390px;
    margin: 0 0 3px;
    font-size: 15px;
    line-height: 1.9;
    color: $muted;
  }
}

.capabilities {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid $line;
  border-bottom: 1px solid $line;
}

.capability {
  min-width: 0;
  padding: 28px 24px 30px 0;

  & + .capability {
    border-left: 1px solid $line;
    padding-left: 24px;
  }

  h3 {
    margin: 0 0 8px;
    font-size: 19px;
    font-weight: 800;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.8;
    color: $muted;
  }
}

.feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 15px;
  border-radius: 6px;

  .icon {
    width: 21px;
    height: 21px;
  }

  &.blue {
    color: #1767db;
    background: #e8f1ff;
  }

  &.orange {
    color: #b7650a;
    background: #fff1da;
  }

  &.coral {
    color: #c85347;
    background: #fff0ed;
  }

  &.green {
    color: #147a6e;
    background: #e6f6f2;
  }
}

.workflow-section {
  background: $pale;
}

.workflow-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid #c4dced;

  li {
    position: relative;
    min-width: 0;
    padding: 28px 24px 0 0;

    & + li {
      border-left: 1px solid #c4dced;
      padding-left: 24px;
    }
  }

  h3 {
    margin: 0 0 9px;
    font-size: 18px;
    font-weight: 800;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.8;
    color: #4a607c;
  }
}

.step-number {
  display: block;
  margin-bottom: 26px;
  color: #1671c9;
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
}

.preview-section {
  padding-top: 52px;
  padding-bottom: 76px;
}

.preview-tabs {
  display: flex;
  align-items: center;
  gap: 28px;
  margin-top: 16px;
  border-bottom: 1px solid $line;

  button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 49px;
    padding: 10px 4px 13px;
    border: 0;
    background: transparent;
    color: #5b6d86;
    font-size: 15px;
    white-space: nowrap;
    cursor: pointer;

    &[aria-selected='true'] {
      color: $blue;
      font-weight: 800;

      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: $blue;
      }
    }

    .icon {
      width: 17px;
      height: 17px;
    }
  }
}

.preview-copy {
  padding: 26px 0 24px;

  h3 {
    margin: 6px 0 5px;
    font-size: 23px;
    line-height: 1.35;
    font-weight: 800;
  }

  p {
    max-width: 670px;
    margin: 0;
    font-size: 14px;
    line-height: 1.75;
    color: $muted;
  }
}

.preview-index {
  font-size: 12px;
  font-weight: 800;
  color: #2380d0;
}

.preview-image-wrap {
  position: relative;
  width: 100%;
  /* 原型示意区为 1400:848，按容器宽度等比留高，不写死像素 */
  padding-top: 60.5714%;
  background: #f5f8fd;
  border: 1px solid #dce6f3;
  border-radius: 4px;
  overflow: hidden;
}

.preview-art {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
}

.preview-note {
  margin: 15px 0 0;
  color: #8290a3;
  font-size: 12px;
}
</style>
